import React, { useMemo } from 'react';
import { Card, Box, Typography, Divider, Chip, Alert } from '@mui/material';
import useCurrency from '../../../../components/CurrencyProvider';

export interface PackageCloserSummaryCardProps {
  /** Total charges before concession */
  total: number;
  /** Concession amount */
  concession: number;
  /** Tax amount */
  tax: number;
  /** Net amount (Misc Total - total of services being charged at closure) */
  net: number;
  /** Credit - Available advance balance (Advance Paid - Consumed) */
  creditAmount: number;
  /** Package statistics from API */
  packageStats?: {
    packageNetAmount?: number;        // Total package value
    packagePaidAmount?: number;       // Advance paid by patient
    packageConsumeAmount?: number;    // Services consumed from package
    remainingAdvanceAmount?: number;  // Remaining advance amount
  };
}

/**
 * Style constants
 */
const STYLES = {
  card: {
    p: 2.5,
    background: '#fafbfc',
    borderRadius: 3,
    boxShadow: 'none',
    transition: 'box-shadow 150ms ease, transform 150ms ease',
    '&:hover, &:focus-within': {
      boxShadow: '0 4px 12px rgba(15, 23, 42, 0.08)',
      transform: 'translateY(-1px)',
    },
  },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: 1.5,
  },
  taxDivider: {
    height: 2,
    background: '#E2E3E5',
    mt: 1,
    borderRadius: 1,
  },
} as const;

/**
 * Settlement result type based on the calculation
 */
type SettlementResult = 'user-pays' | 'user-refund' | 'nothing';

/**
 * Summary card specifically for Package Closer flow
 * 
 * Settlement Logic:
 * - Credit = Advance Remaining = Advance Paid - Consumed
 * - Payable = max(0, Misc Total - Credit) - amount user needs to pay
 * - Refund = max(0, Credit - Misc Total) - amount user receives back
 * 
 * Scenarios:
 * 1. Misc > Credit → User Pays (Misc - Credit)
 * 2. Misc < Credit → User gets Refund (Credit - Misc)
 * 3. Misc = Credit → Nothing (no payment, no refund)
 */
const PackageCloserSummaryCard: React.FC<PackageCloserSummaryCardProps> = ({
  total,
  concession,
  tax,
  net,
  creditAmount,
  packageStats,
}) => {
  const { formatCurrency } = useCurrency();

  // Validation: Check if total charges exceed package amount
  const exceedsPackageAmount = useMemo(() => {
    if (!packageStats?.packageNetAmount) return false;
    return total > packageStats.packageNetAmount;
  }, [total, packageStats?.packageNetAmount]);

  // Calculate payable amount: max(0, Net Amount - Credit)
  // This is what the user needs to pay if net charges exceed available credit
  const payableAmount = useMemo(() => {
    return Math.max(0, net - creditAmount);
  }, [net, creditAmount]);

  // Calculate refund amount: max(0, Credit - Net Amount)
  // This is what the user receives back if credit exceeds net charges
  const refundAmount = useMemo(() => {
    return Math.max(0, creditAmount - net);
  }, [net, creditAmount]);

  // Determine settlement result for display
  const settlementResult: SettlementResult = useMemo(() => {
    if (payableAmount > 0) return 'user-pays';
    if (refundAmount > 0) return 'user-refund';
    return 'nothing';
  }, [payableAmount, refundAmount]);

  // Get result display configuration
  const getResultDisplay = () => {
    switch (settlementResult) {
    case 'user-pays':
      return {
        label: 'Patient Pays',
        color: '#ed6c02' as const, // warning orange
        bgColor: '#fff3e0',
        amount: payableAmount,
      };
    case 'user-refund':
      return {
        label: 'Patient Refund',
        color: '#4caf50' as const, // success green
        bgColor: '#e8f5e9',
        amount: refundAmount,
      };
    case 'nothing':
    default:
      return {
        label: 'Settled',
        color: '#757575' as const, // neutral grey
        bgColor: '#f5f5f5',
        amount: 0,
      };
    }
  };

  const resultDisplay = getResultDisplay();

  // Memoize formatted values
  const formattedValues = useMemo(
    () => ({
      total: formatCurrency(total),
      concession: formatCurrency(concession),
      tax: formatCurrency(tax),
      net: formatCurrency(net),
      credit: formatCurrency(creditAmount),
      payable: formatCurrency(payableAmount),
      refund: formatCurrency(refundAmount),
      settlementAmount: formatCurrency(resultDisplay.amount),
      packageNetAmount: formatCurrency(packageStats?.packageNetAmount ?? 0),
      packagePaidAmount: formatCurrency(packageStats?.packagePaidAmount ?? 0),
      packageConsumeAmount: formatCurrency(packageStats?.packageConsumeAmount ?? 0),
    }),
    [total, concession, tax, net, creditAmount, payableAmount, refundAmount, resultDisplay.amount, formatCurrency, packageStats]
  );

  return (
    <Card
      elevation={0}
      sx={STYLES.card}
      aria-label="Package Closer Summary"
      tabIndex={-1}
    >
      {/* Package Info Section */}
      {packageStats && (
        <>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#666' }}>
            Package Info
          </Typography>
          
          {/* Package Net Amount (Total Package Value) */}
          <Box sx={STYLES.row}>
            <Typography variant="subtitle2">Package Amount</Typography>
            <Typography variant="body2">
              {formattedValues.packageNetAmount}
            </Typography>
          </Box>

          {/* Package Paid Amount (Advance Paid by Patient) */}
          <Box sx={STYLES.row}>
            <Typography variant="subtitle2">Collected Advance Amount</Typography>
            <Typography variant="body2">
              {formattedValues.packagePaidAmount}
            </Typography>
          </Box>

          {/* Package Consume Amount (Services Consumed) */}
          <Box sx={STYLES.row}>
            <Typography variant="subtitle2">Advance Consumed Amount</Typography>
            <Typography variant="body2">
              {formattedValues.packageConsumeAmount}
            </Typography>
          </Box>


          {/* Advance Balance Amount (Credit) */}
          <Box sx={STYLES.row}>
            <Typography variant="subtitle2">Advance Balance Amount</Typography>
            <Typography variant="body2" sx={{ fontWeight: 600, color: creditAmount > 0 ? '#4caf50' : undefined }}>
              {formattedValues.credit}
            </Typography>
          </Box>
          
          <Divider sx={{ my: 2 }} />
        </>
      )}

      {/* Total Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="subtitle2">Total Amount</Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, color: exceedsPackageAmount ? '#d32f2f' : undefined }}>
          {formattedValues.total}
        </Typography>
      </Box>

      {/* Warning: Total exceeds Package Amount */}
      {exceedsPackageAmount && (
        <Alert severity="error" sx={{ mb: 1.5, py: 0.5 }}>
          Total charges cannot exceed Package Amount
        </Alert>
      )}

      {/* Concession Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="subtitle2">Concession Amount</Typography>
        <Typography variant="body2">
          {formattedValues.concession}
        </Typography>
      </Box>

      {/* Tax Amount */}
      <Box sx={{ mb: 1.5 }}>
        <Box sx={STYLES.row}>
          <Typography variant="subtitle2">Tax Amount</Typography>
          <Typography variant="body2">
            {formattedValues.tax}
          </Typography>
        </Box>
        <Box sx={STYLES.taxDivider} />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Net Amount */}
      <Box sx={STYLES.row}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>Net Amount</Typography>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          {formattedValues.net}
        </Typography>
      </Box>

      {/* Payable Amount - only shown when user needs to pay */}
      {payableAmount > 0 && (
        <Box sx={STYLES.row}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ed6c02' }}>Payable Amount</Typography>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#ed6c02' }}>
            {formattedValues.payable}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 2 }} />

      {/* Settlement Section */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5, color: '#666' }}>
        Settlement
      </Typography>

      {/* Payable Amount - shown when user needs to pay */}
      {payableAmount > 0 && (
        <Box sx={STYLES.row}>
          <Typography variant="subtitle2">Amount Payable</Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 700,
              color: '#ed6c02',
            }}
          >
            {formattedValues.payable}
          </Typography>
        </Box>
      )}

      {/* Refund Amount - shown when user gets refund */}
      {refundAmount > 0 && (
        <Box sx={STYLES.row}>
          <Typography variant="subtitle2">Refund Amount</Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 700,
              color: '#4caf50',
            }}
          >
            {formattedValues.refund}
          </Typography>
        </Box>
      )}

      {/* No Payment/Refund - shown when exactly settled */}
      {payableAmount === 0 && refundAmount === 0 && (
        <Box sx={STYLES.row}>
          <Typography variant="subtitle2">Settlement Amount</Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 700,
              color: '#757575',
            }}
          >
            {formatCurrency(0)}
          </Typography>
        </Box>
      )}

      {/* Settlement Result */}
      <Box sx={{ ...STYLES.row, mb: 0 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Result</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip 
            label={resultDisplay.label}
            size="small"
            sx={{ 
              backgroundColor: resultDisplay.bgColor,
              color: resultDisplay.color,
              fontWeight: 600,
              fontSize: '0.75rem',
            }}
          />
          {resultDisplay.amount > 0 && (
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 700,
                color: resultDisplay.color,
              }}
            >
              {formattedValues.settlementAmount}
            </Typography>
          )}
        </Box>
      </Box>
    </Card>
  );
};

export default PackageCloserSummaryCard;
